import { useCallback, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'

const MUSIC_SRC = '/audio/desert-rose-instrumental.mp3'
/**
 * Volumes are intentionally very low — the page has to be safe to open at
 * work or in a quiet room. The mp3 is normalised loud, so 0.06 is roughly
 * a whisper at typical OS volume.
 */
const MUSIC_VOLUME = 0.06
const OCEAN_VOLUME = 0.04

/**
 * Subtle ambience.
 *
 * Music: looped instrumental MP3 served from /audio/.
 * Ocean: synthesised at runtime via Web Audio API (filtered pink noise + slow
 *        gain LFO), so we do not ship an extra binary asset for it.
 *
 * Default ON (very low volume). Browsers block real autoplay until first user
 * interaction; we attempt play() at mount and, if blocked, register a one-shot
 * interaction listener that retries the moment the visitor clicks/taps/keys
 * anywhere on the page (the slideshow's first "next" click counts).
 *
 * Pauses when the tab is hidden, resumes on return if the user wants it on.
 */
export function AudioPanel() {
  const reduced = useReducedMotion()
  const [on, setOn] = useState(true)
  const [actuallyPlaying, setActuallyPlaying] = useState(false)
  const [musicMissing, setMusicMissing] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const oceanStopRef = useRef<(() => void) | null>(null)
  const onRef = useRef(on)

  useEffect(() => {
    onRef.current = on
  }, [on])

  const stop = useCallback(() => {
    audioRef.current?.pause()
    oceanStopRef.current?.()
    oceanStopRef.current = null
    setActuallyPlaying(false)
  }, [])

  const start = useCallback(async (): Promise<boolean> => {
    let played = false
    try {
      if (audioRef.current) {
        audioRef.current.volume = MUSIC_VOLUME
        audioRef.current.loop = true
        try {
          await audioRef.current.play()
          played = true
        } catch (err) {
          if (err instanceof Error && err.name === 'NotAllowedError') {
            // Autoplay blocked — caller should arm interaction listener.
            played = false
          } else if (err instanceof Error && err.name !== 'AbortError') {
            setMusicMissing(true)
          }
        }
      }
      if (!ctxRef.current) {
        const Ctx =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        ctxRef.current = new Ctx()
      }
      if (ctxRef.current.state === 'suspended') {
        try {
          await ctxRef.current.resume()
        } catch {
          /* ignore */
        }
      }
      if (ctxRef.current.state === 'running') {
        oceanStopRef.current = startOcean(ctxRef.current)
        played = true
      }
    } catch {
      /* leave silent */
    }
    setActuallyPlaying(played)
    return played
  }, [])

  const toggle = useCallback(() => {
    setOn((prev) => {
      const next = !prev
      if (next) void start()
      else stop()
      return next
    })
  }, [start, stop])

  // Try to start at mount. If blocked, arm a one-shot interaction listener.
  useEffect(() => {
    let cancelled = false
    let cleanupListeners: (() => void) | null = null

    void (async () => {
      const played = await start()
      if (cancelled || played || !onRef.current) return

      const events = ['pointerdown', 'keydown', 'touchstart'] as const
      const handler = () => {
        if (!onRef.current) return
        void start().then(() => cleanupListeners?.())
      }
      for (const evt of events) {
        document.addEventListener(evt, handler, { passive: true, once: false })
      }
      cleanupListeners = () => {
        for (const evt of events) document.removeEventListener(evt, handler)
        cleanupListeners = null
      }
    })()

    return () => {
      cancelled = true
      cleanupListeners?.()
      stop()
      ctxRef.current?.close().catch(() => {})
      ctxRef.current = null
    }
  }, [start, stop])

  // Pause on tab blur, resume on focus if the user wants it on.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) stop()
      else if (onRef.current) void start()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [start, stop])

  // Detect missing music file early.
  useEffect(() => {
    const a = audioRef.current
    if (!a) return
    const onErr = () => setMusicMissing(true)
    a.addEventListener('error', onErr)
    return () => a.removeEventListener('error', onErr)
  }, [])

  return (
    <div
      className="pointer-events-none fixed right-4 z-50 flex flex-col items-end gap-2 sm:right-6"
      style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
    >
      <audio
        ref={audioRef}
        src={MUSIC_SRC}
        preload="auto"
        playsInline
        className="hidden"
      />

      <AnimatePresence>
        {on && (
          <motion.div
            key="caption"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            className="pointer-events-auto rounded-full border border-white/20 bg-black/60 px-3 py-1 font-hand text-sm text-[var(--color-cream)]/85 backdrop-blur"
          >
            {musicMissing
              ? 'waves only'
              : actuallyPlaying
                ? 'music + waves · low'
                : 'tap anywhere to start sound'}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={toggle}
        aria-pressed={on}
        aria-label={on ? 'Mute music and waves' : 'Unmute music and waves'}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className={`pointer-events-auto relative grid h-12 w-12 place-items-center rounded-full border shadow-[0_10px_30px_-12px_rgba(0,0,0,0.7)] transition-colors ${
          on
            ? 'border-[var(--color-crimson)] bg-[var(--color-crimson)] text-[var(--color-cream)]'
            : 'border-white/25 bg-black/60 text-[var(--color-cream)]/80 hover:bg-black/75'
        }`}
      >
        <span aria-hidden="true" className="text-lg">
          {on ? '🎶' : '🔇'}
        </span>
        {on && actuallyPlaying && !reduced && (
          <span aria-hidden="true" className="pointer-events-none absolute inset-0">
            {[0, 1].map((i) => (
              <motion.span
                key={i}
                className="absolute inset-0 rounded-full border border-white/45"
                initial={{ opacity: 0.6, scale: 1 }}
                animate={{ opacity: 0, scale: 1.6 }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: 'easeOut',
                  delay: i * 1.2,
                }}
              />
            ))}
          </span>
        )}
      </motion.button>
    </div>
  )
}

/**
 * Procedural ocean: filtered pink-noise loop with a slow LFO on the gain so
 * it breathes like distant waves. Returns a stop function.
 */
function startOcean(ctx: AudioContext): () => void {
  const noise = ctx.createBufferSource()
  noise.buffer = makePinkNoiseBuffer(ctx, 4)
  noise.loop = true

  const lowpass = ctx.createBiquadFilter()
  lowpass.type = 'lowpass'
  lowpass.frequency.value = 800
  lowpass.Q.value = 0.7

  const highpass = ctx.createBiquadFilter()
  highpass.type = 'highpass'
  highpass.frequency.value = 90
  highpass.Q.value = 0.5

  const swellGain = ctx.createGain()
  swellGain.gain.value = OCEAN_VOLUME

  const lfoA = ctx.createOscillator()
  lfoA.type = 'sine'
  lfoA.frequency.value = 0.13

  const lfoB = ctx.createOscillator()
  lfoB.type = 'sine'
  lfoB.frequency.value = 0.07

  const lfoGainA = ctx.createGain()
  lfoGainA.gain.value = OCEAN_VOLUME * 0.5
  const lfoGainB = ctx.createGain()
  lfoGainB.gain.value = OCEAN_VOLUME * 0.3

  lfoA.connect(lfoGainA).connect(swellGain.gain)
  lfoB.connect(lfoGainB).connect(swellGain.gain)

  noise.connect(highpass).connect(lowpass).connect(swellGain).connect(ctx.destination)

  noise.start()
  lfoA.start()
  lfoB.start()

  if (ctx.state === 'suspended') void ctx.resume()

  return () => {
    try { noise.stop() } catch { /* already stopped */ }
    try { lfoA.stop() } catch { /* already stopped */ }
    try { lfoB.stop() } catch { /* already stopped */ }
    try {
      noise.disconnect()
      highpass.disconnect()
      lowpass.disconnect()
      swellGain.disconnect()
      lfoGainA.disconnect()
      lfoGainB.disconnect()
    } catch {
      /* already disconnected */
    }
  }
}

function makePinkNoiseBuffer(ctx: AudioContext, seconds: number) {
  const sampleRate = ctx.sampleRate
  const length = Math.floor(seconds * sampleRate)
  const buffer = ctx.createBuffer(1, length, sampleRate)
  const data = buffer.getChannelData(0)

  let b0 = 0
  let b1 = 0
  let b2 = 0
  let b3 = 0
  let b4 = 0
  let b5 = 0
  let b6 = 0
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1
    b0 = 0.99886 * b0 + white * 0.0555179
    b1 = 0.99332 * b1 + white * 0.0750759
    b2 = 0.969 * b2 + white * 0.153852
    b3 = 0.8665 * b3 + white * 0.3104856
    b4 = 0.55 * b4 + white * 0.5329522
    b5 = -0.7616 * b5 - white * 0.016898
    const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362
    b6 = white * 0.115926
    data[i] = pink * 0.11
  }
  return buffer
}
